import { creatorbase } from '@round/api';
import { getPlatformAccount } from '../PostForm/PostForm';
import { CreatorSelectOption } from '../PostForm/useCreatorSelect';
import {
    getNormalizedPaymentCost,
    isCreatorCostMismatched,
} from 'Modules/Plans/Posts/components/CreatorCostMismatchBanner/helpers';

export type MismatchAfterSave = {
    clientInfluencerId: string;
    platform: Extract<creatorbase.Platform, 'tiktok' | 'instagram'>;
    paymentCost: number;
    paymentCurrencyId: number;
};

type Params = {
    platform: creatorbase.Platform | null | undefined;
    creator: CreatorSelectOption | null | undefined;
    // MoneyField writes the raw CurrencyInput value, so this can be a string while typing.
    cost: number | string | null | undefined;
    currencyId: number | null | undefined;
};

/**
 * Only known once the post has actually been saved - checking while the user is still typing
 * would flag values they haven't committed to yet.
 */
export const getMismatchAfterSave = ({ platform, creator, cost, currencyId }: Params): MismatchAfterSave | null => {
    const mismatchablePlatform = platform === 'tiktok' || platform === 'instagram' ? platform : null;
    if (!mismatchablePlatform) {
        return null;
    }

    const platformAccount = getPlatformAccount(creator, mismatchablePlatform);
    if (!platformAccount) {
        return null;
    }

    // The banner's only action patches the creator record, so without an id it renders nothing -
    // which would leave the modal held open by a mismatch the user can't see or act on.
    const clientInfluencerId = creator?.creatorbase_client_influencer_id;
    if (!clientInfluencerId) {
        return null;
    }

    const paymentCost = getNormalizedPaymentCost(cost);
    const isMismatched = isCreatorCostMismatched(
        platformAccount.cost,
        platformAccount.currency_id,
        paymentCost,
        currencyId
    );
    if (!isMismatched || paymentCost === null || !currencyId) {
        return null;
    }

    return {
        clientInfluencerId,
        platform: mismatchablePlatform,
        paymentCost,
        paymentCurrencyId: currencyId,
    };
};
